import { RulesReadmodelType, RuleDefinitionsReadmodelType } from '@appTypes/Rules';
import { UserRole } from '@appTypes/Users';
import { AccountsReadmodelType, BillingEntitiesReadmodelType, BillingEntityPointOfSaleType, OrderStatusType, SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';

/**
 * Canonical order status (per-account configurable, WS-2 / TV1-1888).
 * Mirrors `OrderStatusesReadmodelSchema` in teselly-domain:
 * `{ id, name: { es, en, pt }, description?, color?, order }`.
 */
export type OrderStatus = OrderStatusType;

/**
 * Account tab type
 */
export type AccountTab = 'accountInfo' | 'rules' | 'billing' | 'users' | 'security' | 'itemsPublishing' | 'orderStatuses';

/**
 * The SELLER's tax regime, as offered on the billing-entity form.
 *
 * TV1-2300 — narrowed from 11 members to 3, mirroring
 * `BillingEntityAddedEventSchema.vatCondition` in `@bett3r-dev/teselly-domain`
 * (the 11 were ARCA's *buyer* table; ~8 are impossible for a seller). This is
 * the MECHANICAL narrowing only — TV1-2203 owns the form's semantics
 * (required-ness, ordering, help text).
 */
export type VatCondition =
  | 'responsableInscripto'
  | 'monotributista'
  | 'exento';

/**
 * User profile type — mirrors UserProfileReadmodel. Roles do NOT live here;
 * they're per-membership and read from AuthUserSecurityReadmodel.memberships
 * via state.users.membershipRoles.
 */
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  accountId: string;
}

/**
 * Billing entity form data
 */
export interface BillingEntityFormData {
  name: string;
  legalName: string;
  documentId: string;
  billingAddress: string;
  vatCondition?: VatCondition;
  grossIncome?: string;
  activityStartDate?: string;
  /**
   * TV1-2210 (S6) — the STRUCTURED ARCA record, carried through the form.
   *
   * S4 kept this as `string[]` and converted at the submit boundary, which was
   * lossless only while nothing ever populated `emisionTipo` / `bloqueado` /
   * `fchBaja`. The S6 picker reads them from `FEParamGetPtosVenta`, so an edit
   * round trip through a `string[]` form would now DISCARD three ARCA-owned
   * fields the user never sees and cannot re-author. The form therefore holds
   * the record itself; `number` is still the only field a user selects.
   */
  pointsOfSale: BillingEntityPointOfSaleType[];
  taxAgency: 'arca' | string;
  /** id of the SystemIntegration (issues-invoices) this billing entity issues through (1:1) */
  invoicingIntegrationId: string;
}

/**
 * User form data
 */
export interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
}

/**
 * Form mode discriminated union
 */
export type FormMode =
  | { type: 'closed' }
  | { type: 'add' }
  | { type: 'edit'; entityId: string };

/**
 * Assignment group (mirrors AssignmentGroupsReadmodel — TV1-1865/1878).
 * Account-scoped; `memberUserIds` drives the `assigned` scope on the backend.
 */
export interface AssignmentGroup {
  id: string;
  accountId: string;
  name: string;
  memberUserIds: string[];
}

/**
 * Assignment-groups form mode: create (add) or rename an existing group.
 */
export type GroupFormMode =
  | { type: 'closed' }
  | { type: 'add' }
  | { type: 'rename'; groupId: string };

/**
 * Assignment-group form data — name plus the batch-managed membership selection
 * (applied on save, mimicking the Users form). TV1-1878.
 */
export interface GroupFormData {
  name: string;
  memberUserIds: string[];
}

/**
 * Empty assignment-group form data.
 */
export const emptyGroupFormData: GroupFormData = {
  name: '',
  memberUserIds: []
};

/**
 * SKU config category type
 */
export type SkuConfigCategory = {
  id: string;
  name: string;
  defaultSkuFormula: string | null;
};

/**
 * Main account state
 */
export interface AccountState {
  /** Tab navigation (synced from page) */
  activeTab: AccountTab;

  /** Account data (from subscription) */
  account: AccountsReadmodelType | null;

  /** AccountInfo tab state */
  accountInfo: {
    editingField: string | null; // Currently editing field name
    tempValue: string; // Temporary value while editing
    loading: {
      saving: boolean;
    };
  };

  /** AccountBilling tab state */
  billing: {
    entities: Record<string, BillingEntitiesReadmodelType>;
    /** Provisioned invoicing integrations (connector declares `issues-invoices`),
     * loaded cross-domain by the billing sync policy. Drives the invoicing-integration
     * selector in the billing-entity form. */
    invoicingIntegrations: Record<string, SystemIntegrationsReadmodel>;
    searchQuery: string;
    currentPage: number;

    // Form state
    formMode: FormMode;
    formData: BillingEntityFormData;
    formErrors: Record<string, string>;

    /**
     * TV1-2210 (S6) — the ARCA-registered points of sale for the invoicing
     * integration currently selected on the form (`FEParamGetPtosVenta`, via
     * `GET /billing-entity-points-of-sale`). The picker offers THESE and nothing
     * else: a point of sale a user types is not usable, because ARCA does not
     * auto-register one when you invoice with it and production rejects it with
     * `11002` — an error homologación cannot reproduce.
     *
     * `integrationId` records WHICH integration `items` belongs to, so a stale
     * list is never offered against a freshly-selected integration.
     */
    arcaPointsOfSale: {
      integrationId: string | null;
      items: BillingEntityPointOfSaleType[];
      loading: boolean;
      loaded: boolean;
      error?: string;
    };

    loading: {
      entities: boolean;
      submitting: boolean;
    };
  };

  /** AccountUsers tab state */
  users: {
    list: UserProfile[];
    /** Per-user current membership roles for the active account, sourced from
     * AuthUserSecurityReadmodel.memberships[accountId]. */
    membershipRoles: Record<string, string[]>;
    searchQuery: string;
    currentPage: number;

    // Form state
    formMode: FormMode;
    formData: UserFormData;
    formErrors: Record<string, string>;

    loading: {
      fetching: boolean;
      submitting: boolean;
    };
  };

  /** AssignmentGroups tab state (TV1-1878) */
  assignmentGroups: {
    list: AssignmentGroup[];
    searchQuery: string;

    // Form state (create / rename) — membership is managed INSIDE the form and
    // applied on save (batch), mimicking the Users form.
    formMode: GroupFormMode;
    formData: GroupFormData;
    formErrors: Record<string, string>;

    /** Pending backend command for the submit policy to act on (TV1-1878).
     * `create` carries no id — the policy mints the new group id. Both create
     * and rename carry the full selected membership; the policy syncs members
     * (create: add each; rename: diff against the current group). */
    pendingCommand:
      | { kind: 'create'; name: string; memberUserIds: string[] }
      | { kind: 'rename'; groupId: string; name: string; memberUserIds: string[] }
      | { kind: 'delete'; groupId: string }
      | null;

    loading: {
      fetching: boolean;
      submitting: boolean;
    };
  };

  /** AccountRules tab state */
  rules: {
    definitions: RuleDefinitionsReadmodelType[];
    appliedRules: RulesReadmodelType[];
    openAddTrigger: number;

    loading: {
      definitions: boolean;
      appliedRules: boolean;
      submitting: boolean;
    };
  };

  /** Top-level loading */
  loading: {
    account: boolean;
  };

  /** SKU Configuration tab state */
  skuConfig: {
    categories: SkuConfigCategory[];
    overrides: Record<string, string>; // categoryId -> formula
    isLoading: boolean;
    isSaving: string | null; // categoryId being saved, or null
    search: string;
    editingCategory: { id: string; formula: string } | null;
  };

  /** Order Statuses tab state (WS-2 / TV1-1888) */
  orderStatuses: {
    /** Persisted catalog (from SalesAccountConfigurationReadmodel / defaults). */
    data: OrderStatus[];
    isLoading: boolean;
    /** Working copy while editing; null means "not editing". */
    draft: OrderStatus[] | null;
    isSaving: boolean;
    error?: string;
  };
}

/**
 * Empty billing entity form data
 */
export const emptyBillingEntityFormData: BillingEntityFormData = {
  name: '',
  legalName: '',
  documentId: '',
  billingAddress: '',
  vatCondition: undefined,
  grossIncome: '',
  activityStartDate: '',
  pointsOfSale: [],
  taxAgency: 'arca',
  invoicingIntegrationId: ''
};

/**
 * TV1-2210 (S6) — the un-fetched ARCA points-of-sale list.
 */
export const emptyArcaPointsOfSale = {
  integrationId: null,
  items: [],
  loading: false,
  loaded: false
} satisfies AccountState['billing']['arcaPointsOfSale'];

/**
 * Empty user form data
 */
export const emptyUserFormData: UserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  roles: []
};

/**
 * Initial state factory
 */
export const initialState: AccountState = {
  activeTab: 'accountInfo',

  account: null,

  accountInfo: {
    editingField: null,
    tempValue: '',
    loading: {
      saving: false
    }
  },

  billing: {
    entities: {},
    invoicingIntegrations: {},
    searchQuery: '',
    currentPage: 1,

    formMode: { type: 'closed' },
    formData: emptyBillingEntityFormData,
    formErrors: {},
    arcaPointsOfSale: emptyArcaPointsOfSale,

    loading: {
      entities: false,
      submitting: false
    }
  },

  users: {
    list: [],
    membershipRoles: {},
    searchQuery: '',
    currentPage: 1,

    formMode: { type: 'closed' },
    formData: emptyUserFormData,
    formErrors: {},

    loading: {
      fetching: false,
      submitting: false
    }
  },

  assignmentGroups: {
    list: [],
    searchQuery: '',

    formMode: { type: 'closed' },
    formData: emptyGroupFormData,
    formErrors: {},

    pendingCommand: null,

    loading: {
      fetching: false,
      submitting: false
    }
  },

  rules: {
    definitions: [],
    appliedRules: [],
    openAddTrigger: 0,

    loading: {
      definitions: false,
      appliedRules: false,
      submitting: false
    }
  },

  loading: {
    account: false
  },

  skuConfig: {
    categories: [],
    overrides: {},
    isLoading: false,
    isSaving: null,
    search: '',
    editingCategory: null
  },

  orderStatuses: {
    data: [],
    isLoading: false,
    draft: null,
    isSaving: false,
    error: undefined
  }
};

/**
 * VAT condition options for dropdowns
 */
export const vatConditionOptions: Array<{ value: VatCondition; label: string }> = [
  { value: 'responsableInscripto', label: 'Responsable Inscripto' },
  { value: 'monotributista', label: 'Monotributista' },
  { value: 'exento', label: 'Exento' }
];

/**
 * Derived state selectors
 */
export const selectors = {
  /** Get filtered billing entities by search query */
  filteredBillingEntities: ( state: AccountState ): BillingEntitiesReadmodelType[] => {
    const entities = Object.values( state.billing.entities );
    const query = state.billing.searchQuery.toLowerCase().trim();

    if ( !query ) return entities;

    return entities.filter(( entity ) => {
      const posMatch = Array.isArray( entity.pointsOfSale ) &&
        // TV1-2210 (S4) — points of sale are STRUCTURED records now; match on the
        // number's decimal form. No `toLowerCase` (digits have no case), and `'01'`
        // no longer matches anything because it can no longer be stored.
        entity.pointsOfSale.some(( p ) => String( p?.number ).includes( query ));
      const fields = [
        entity.name,
        entity.legalName,
        entity.documentId,
        entity.billingAddress,
        entity.grossIncome,
        entity.activityStartDate,
        entity.vatCondition as string | undefined,
        entity.taxAgency,
        entity.id
      ];
      return fields.some(( v ) => v?.toString()?.toLowerCase()
.includes( query )) || posMatch;
    });
  },

  /** Get total pages for billing entities */
  billingTotalPages: ( state: AccountState, itemsPerPage: number = 5 ): number => {
    const filtered = selectors.filteredBillingEntities( state );
    return Math.max( 1, Math.ceil( filtered.length / itemsPerPage ));
  },

  /** Get paginated billing entities */
  paginatedBillingEntities: ( state: AccountState, itemsPerPage: number = 5 ): BillingEntitiesReadmodelType[] => {
    const filtered = selectors.filteredBillingEntities( state );
    const start = ( state.billing.currentPage - 1 ) * itemsPerPage;
    const end = start + itemsPerPage;
    return filtered.slice( start, end );
  },

  /** Get billing entity by ID */
  getBillingEntityById: ( state: AccountState, id: string ): BillingEntitiesReadmodelType | undefined => {
    return state.billing.entities[id];
  },

  /** Check if billing form is valid */
  isBillingFormValid: ( state: AccountState ): boolean => {
    const { formData } = state.billing;
    return (
      formData.name.trim().length > 0 &&
      formData.legalName.trim().length > 0 &&
      formData.documentId.trim().length > 0 &&
      formData.billingAddress.trim().length > 0 &&
      formData.invoicingIntegrationId.trim().length > 0
    );
  },

  /**
   * Set of invoicingIntegrationId values already linked to a billing entity OTHER
   * than the one currently being edited. Mirrors the backend 1:1 uniqueness rule.
   */
  linkedInvoicingIntegrationIds: ( state: AccountState ): Set<string> => {
    const editingEntityId =
      state.billing.formMode.type === 'edit' ? state.billing.formMode.entityId : null;
    const linked = new Set<string>();
    for ( const entity of Object.values( state.billing.entities )) {
      const id = entity.invoicingIntegrationId;
      if ( id && entity.id !== editingEntityId ) linked.add( id );
    }
    return linked;
  },

  /**
   * Invoicing integrations selectable in the billing-entity form: all provisioned
   * invoicing integrations minus those already linked to another billing entity
   * (the current form's own selection is always kept). Mirrors the backend 1:1
   * rule so the user never hits a submit-time ALREADY_LINKED error.
   */
  availableInvoicingIntegrations: ( state: AccountState ): SystemIntegrationsReadmodel[] => {
    const linked = selectors.linkedInvoicingIntegrationIds( state );
    const current = state.billing.formData.invoicingIntegrationId;
    return Object.values( state.billing.invoicingIntegrations ).filter(
      ( integration ) => integration.id === current || !linked.has( integration.id )
    );
  },

  /** Get filtered users by search query */
  filteredUsers: ( state: AccountState ): UserProfile[] => {
    const query = state.users.searchQuery.toLowerCase().trim();

    if ( !query ) return state.users.list;

    return state.users.list.filter(( user ) => {
      return (
        user.email?.toLowerCase().includes( query ) ||
        user.firstName?.toLowerCase().includes( query ) ||
        user.lastName?.toLowerCase().includes( query ) ||
        user.id?.toLowerCase().includes( query )
      );
    });
  },

  /** Get total pages for users */
  usersTotalPages: ( state: AccountState, itemsPerPage: number = 9 ): number => {
    const filtered = selectors.filteredUsers( state );
    return Math.max( 1, Math.ceil( filtered.length / itemsPerPage ));
  },

  /** Get paginated users */
  paginatedUsers: ( state: AccountState, itemsPerPage: number = 9 ): UserProfile[] => {
    const filtered = selectors.filteredUsers( state );
    const start = ( state.users.currentPage - 1 ) * itemsPerPage;
    const end = start + itemsPerPage;
    return filtered.slice( start, end );
  },

  /** Get user by ID */
  getUserById: ( state: AccountState, id: string ): UserProfile | undefined => {
    return state.users.list.find(( u ) => u.id === id );
  },

  /** Check if user form is valid */
  isUserFormValid: ( state: AccountState ): boolean => {
    const { formData } = state.users;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      formData.email.trim().length > 0 &&
      emailRegex.test( formData.email.trim()) &&
      formData.firstName.trim().length > 0 &&
      formData.lastName.trim().length > 0 &&
      formData.roles.length > 0
    );
  },

  /** Get rule by ID */
  getRuleById: ( state: AccountState, id: string ): RulesReadmodelType | undefined => {
    return state.rules.appliedRules.find(( r ) => r.id === id || r.ruleId === id );
  },

  // ---------------------------------------------------------------------------
  // Assignment groups (TV1-1878)
  // ---------------------------------------------------------------------------

  /** Get assignment groups filtered by the groups search query. */
  filteredGroups: ( state: AccountState ): AssignmentGroup[] => {
    const query = state.assignmentGroups.searchQuery.toLowerCase().trim();
    if ( !query ) return state.assignmentGroups.list;
    return state.assignmentGroups.list.filter(
      ( g ) => g.name.toLowerCase().includes( query ) || g.id.toLowerCase().includes( query )
    );
  },

  /** Get an assignment group by ID. */
  getGroupById: ( state: AccountState, id: string ): AssignmentGroup | undefined => {
    return state.assignmentGroups.list.find(( g ) => g.id === id );
  },

  /** Options for the group-form members MultiSelect: every account user as
   * `{ value: id, label: fullName || email }`. TV1-1878. */
  groupFormUserOptions: ( state: AccountState ): Array<{ value: string; label: string }> =>
    state.users.list.map(( u ) => ({
      value: u.id,
      label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email
    })),

  // ---------------------------------------------------------------------------
  // Order statuses (WS-2 / TV1-1888)
  // ---------------------------------------------------------------------------

  /**
   * The order statuses to render: the working `draft` while editing, otherwise
   * the persisted `data`. Sorted by `order` for a stable display.
   */
  selectOrderStatuses: ( state: AccountState ): OrderStatus[] => {
    const source = state.orderStatuses.draft ?? state.orderStatuses.data;
    return [...source].sort(( a, b ) => a.order - b.order );
  },

  /**
   * True when an in-progress draft differs from the persisted data — drives the
   * Save / Discard affordances.
   */
  selectIsOrderStatusesDirty: ( state: AccountState ): boolean => {
    const { draft, data } = state.orderStatuses;
    if ( draft === null ) return false;
    return JSON.stringify( draft ) !== JSON.stringify( data );
  }
};
